/**
 * What is on the selection, and how to change it.
 *
 * Contextual rather than a permanent ribbon: the controls that appear
 * are the ones that apply to what is selected, so a connector never
 * offers a fill and a shape never offers an arrowhead. A bar that shows
 * everything and disables most of it teaches people to stop reading it.
 *
 * The palette is the colours the real charts are actually drawn in —
 * white parties, pink life estates, yellow interest callouts, red
 * examiner notes — with a full picker behind them. A swatch row of the
 * ten colours somebody uses is faster than a picker every time, and the
 * picker is still there for the eleventh.
 */
import { useMemo } from 'react';
import ColorPick from './ColorPick.jsx';
import {
  align, bringToFront, distribute, sendToBack, updateEdges, updateShapes,
} from '../../lib/canvas/document.js';
import './StyleBar.css';
import { MAX_FONT, MIN_FONT, clampFont, stepFont } from '../../lib/canvas/fonts.js';

// Drawn from the charts these replace, so the common case is one click.
const FILLS = [
  { value: '#ffffff', title: 'White — a party' },
  { value: '#ff80df', title: 'Pink — a life estate' },
  { value: '#fcfcca', title: 'Yellow — an interest' },
  { value: '#c8e6c9', title: 'Green — leased' },
  { value: '#bbdefb', title: 'Blue' },
  { value: '#ffcdd2', title: 'Red' },
  { value: '#e0e0e0', title: 'Grey' },
  { value: 'transparent', title: 'No fill' },
];

const LINES = [
  { value: '#3a414a', title: 'Slate — the default on every chart' },
  { value: '#e81313', title: 'Red — something to check' },
  { value: '#2f6df6', title: 'Blue' },
  { value: '#12805c', title: 'Green' },
  { value: '#000000', title: 'Black' },
  { value: 'transparent', title: 'No line' },
];

const WIDTHS = [1, 2, 3, 4];
const STROKES = [
  { value: 'solid', label: '——' },
  { value: 'dashed', label: '– –' },
  { value: 'dotted', label: '· ·' },
];

export default function StyleBar({ doc, selection, onChange, onCommit }) {
  const shapes = useMemo(
    () => doc.shapes.filter((s) => selection.includes(s.id)), [doc.shapes, selection],
  );
  const edges = useMemo(
    () => doc.edges.filter((e) => selection.includes(e.id)), [doc.edges, selection],
  );
  // The bar KEEPS ITS PLACE when nothing is selected. Rendering nothing
  // makes the canvas jump up and down every time somebody clicks a box,
  // and on a dense chain the thing you were looking at moves out from
  // under the pointer at the moment you select it.
  if (!shapes.length && !edges.length) {
    return (
      <div className="sb is-empty" role="toolbar" aria-label="Style">
        <span className="sb-label">Select a shape or a connector to style it</span>
      </div>
    );
  }

  const apply = (patch, label) => {
    let next = doc;
    if (shapes.length) next = updateShapes(next, shapes.map((s) => s.id), patch);
    if (edges.length) next = updateEdges(next, edges.map((e) => e.id), patch);
    onChange(next);
    onCommit?.(label);
  };

  const run = (fn, label) => { onChange(fn(doc, selection)); onCommit?.(label); };

  // What the controls SHOW is the first selected item's value. Showing
  // nothing for a mixed selection would make the bar look broken; the
  // first is at least a real value somebody can see on screen.
  const lead = shapes[0] || edges[0];
  const onlyEdges = !shapes.length;

  const size = Number(lead.fontSize) || 10;
  const setSize = (value) => apply({ fontSize: clampFont(value) }, 'Font size');

  return (
    <div className="sb" role="toolbar" aria-label="Style">
      {/* TEXT FIRST. The bar scrolls sideways rather than wrapping, so
          whatever sits at the end is what nobody finds — and font size
          was at the end, off the edge, behind a shape palette. The
          controls people reach for most often go where they can be seen
          without looking for them. */}
      <span className="sb-label">Text</span>
      <button
        type="button" className="sb-btn" aria-label="Smaller text"
        title="Smaller (⌘⇧,)" onClick={() => setSize(stepFont(size, -1))}
      >
        A−
      </button>
      {/* A number, not a menu: 10 and 8 are what a party box and a deed
          block are set in, but an examiner's note across a whole section
          is 50, and a fixed list would refuse it. */}
      <input
        type="number"
        className="sb-size"
        aria-label="Font size"
        min={MIN_FONT}
        max={MAX_FONT}
        value={size}
        onChange={(e) => setSize(Number(e.target.value))}
      />
      <button
        type="button" className="sb-btn" aria-label="Bigger text"
        title="Bigger (⌘⇧.)" onClick={() => setSize(stepFont(size, 1))}
      >
        A+
      </button>
      <button
        type="button" className={`sb-btn${lead.bold ? ' is-on' : ''}`} aria-label="Bold"
        aria-pressed={Boolean(lead.bold)} onClick={() => apply({ bold: !lead.bold }, 'Bold')}
      >
        <strong>B</strong>
      </button>
      {!onlyEdges && (
        <>
          <button
            type="button" className={`sb-btn${lead.italic ? ' is-on' : ''}`} aria-label="Italic"
            aria-pressed={Boolean(lead.italic)} onClick={() => apply({ italic: !lead.italic }, 'Italic')}
          >
            <em>I</em>
          </button>
          <button
            type="button" className={`sb-btn${lead.underline ? ' is-on' : ''}`} aria-label="Underline"
            aria-pressed={Boolean(lead.underline)} onClick={() => apply({ underline: !lead.underline }, 'Underline')}
          >
            <span style={{ textDecoration: 'underline' }}>U</span>
          </button>
        </>
      )}
      <ColorPick
        label="Text colour"
        options={LINES}
        value={onlyEdges ? lead.labelColor : lead.textColor}
        onPick={(v) => apply(onlyEdges ? { labelColor: v } : { textColor: v }, 'Text colour')}
      />

      <span className="sb-gap" />

      {/* A fill is a shape's alone. A connector has no inside. */}
      {!onlyEdges && (
        <>
          <span className="sb-label">Fill</span>
          <ColorPick label="Fill" options={FILLS} value={lead.fill} onPick={(v) => apply({ fill: v }, 'Fill')} />
        </>
      )}

      <span className="sb-label">Line</span>
      <ColorPick label="Line" options={LINES} value={lead.stroke} onPick={(v) => apply({ stroke: v }, 'Line')} />

      <select
        aria-label="Line width"
        value={lead.strokeWidth}
        onChange={(e) => apply({ strokeWidth: Number(e.target.value) }, 'Line width')}
      >
        {WIDTHS.map((w) => <option key={w} value={w}>{`${w}px`}</option>)}
      </select>

      <select
        aria-label="Line style"
        value={lead.strokeStyle}
        onChange={(e) => apply({ strokeStyle: e.target.value }, 'Line style')}
      >
        {STROKES.map((st) => <option key={st.value} value={st.value}>{st.label}</option>)}
      </select>

      {/* An arrowhead is a connector's alone, and both ends are asked
          about because a chain sometimes needs one pointing back. */}
      {edges.length > 0 && (
        <>
          <span className="sb-gap" />
          <span className="sb-label">Ends</span>
          <button
            type="button" className={`sb-btn${lead.startCap === 'arrow' ? ' is-on' : ''}`}
            aria-label="Arrow at the start" aria-pressed={lead.startCap === 'arrow'}
            onClick={() => apply({ startCap: lead.startCap === 'arrow' ? 'none' : 'arrow' }, 'Arrowhead')}
          >
            ◄—
          </button>
          <button
            type="button" className={`sb-btn${lead.endCap === 'arrow' ? ' is-on' : ''}`}
            aria-label="Arrow at the end" aria-pressed={lead.endCap === 'arrow'}
            onClick={() => apply({ endCap: lead.endCap === 'arrow' ? 'none' : 'arrow' }, 'Arrowhead')}
          >
            —►
          </button>
          <select
            aria-label="Line shape"
            value={lead.lineShape}
            onChange={(e) => apply({ lineShape: e.target.value }, 'Line shape')}
          >
            <option value="elbow">Elbow</option>
            <option value="straight">Straight</option>
          </select>
        </>
      )}

      <span className="sb-gap" />
      <button type="button" className="sb-btn" aria-label="Bring to front" title="Bring to front" onClick={() => run(bringToFront, 'Bring to front')}>⬆</button>
      <button type="button" className="sb-btn" aria-label="Send to back" title="Send to back" onClick={() => run(sendToBack, 'Send to back')}>⬇</button>

      {/* Aligning one shape to itself is a no-op, so the controls only
          appear once there is something to align it TO. */}
      {shapes.length > 1 && (
        <>
          <span className="sb-gap" />
          <span className="sb-label">Align</span>
          {[
            ['left', 'Left', '⇤'], ['hcenter', 'Centre', '↔'], ['right', 'Right', '⇥'],
            ['top', 'Top', '⇡'], ['vcenter', 'Middle', '↕'], ['bottom', 'Bottom', '⇣'],
          ].map(([how, title, glyph]) => (
            <button
              key={how} type="button" className="sb-btn" title={title} aria-label={`Align ${title.toLowerCase()}`}
              onClick={() => run((d, ids) => align(d, ids, how), 'Align')}
            >
              {glyph}
            </button>
          ))}
        </>
      )}
      {shapes.length > 2 && (
        <>
          <button type="button" className="sb-btn" title="Space evenly across" aria-label="Distribute horizontally" onClick={() => run((d, ids) => distribute(d, ids, 'x'), 'Distribute')}>≡</button>
          <button type="button" className="sb-btn" title="Space evenly down" aria-label="Distribute vertically" onClick={() => run((d, ids) => distribute(d, ids, 'y'), 'Distribute')}>⋮</button>
        </>
      )}
    </div>
  );
}
